import moment from 'moment';
import { use, useMemo } from 'react';

import { ClubCmsContent } from 'src/content/club-content-consumer';
import { SiteContext } from 'src/context/site-context';
import { useIsClient } from 'src/custom-hooks/use-client-snapshot';
import type { LanguageIsoCode } from 'src/enums/language';
import {
  DEFAULT_TRANSLATION_LANGUAGE_CODE,
  obtainTranslationDataForLanguage,
} from 'src/helpers/combine-translation-data';
import type { PromotionContent } from 'src/models/cms/promotions-content';

const getTimedPromotionForClub = (
  siteId: string,
  promotions: Record<LanguageIsoCode, PromotionContent>[]
) => {
  const now = moment();

  return promotions?.find((promotion) => {
    const timedPromotion = promotion[DEFAULT_TRANSLATION_LANGUAGE_CODE];
    const isPromotionForCurrentClub = timedPromotion.siteIds.includes(siteId);
    const isActive =
      moment(timedPromotion.promotionDetails.to).isAfter(now) &&
      moment(timedPromotion.promotionDetails.from).isBefore(now);

    return isActive && isPromotionForCurrentClub;
  });
};

export const useTimedPromotionForClub = () => {
  const { promotionsContent } = use(ClubCmsContent);
  const { siteId, language } = use(SiteContext);

  const livePromotionContent = useMemo<PromotionContent | undefined>(() => {
    const activeTimedPromotionForClub = getTimedPromotionForClub(siteId, promotionsContent);
    if (!activeTimedPromotionForClub) {
      return undefined;
    }
    return obtainTranslationDataForLanguage(activeTimedPromotionForClub, language);
  }, [language, promotionsContent, siteId]);

  // The timed promotion might become inactive between static generation and client-side hydration, so don't render it
  // initially.
  const isReady = useIsClient();
  const promotionContent = isReady ? livePromotionContent : undefined;

  const useTimedLandingPromo = promotionContent?.promotionDetails?.landing?.applyPromotionContent;
  const useTimedEnquirePromo = promotionContent?.promotionDetails?.enquire?.applyPromotionContent;
  const useTimedPromoPopup = promotionContent?.promotionDetails?.popup?.applyPromotionContent;
  const showGlobalBanner = promotionContent?.promotionDetails?.globalBanner?.showBanner;

  return {
    isTimedPromotionReady: isReady,
    timedPromoActiveForCurrentClub: promotionContent,
    useTimedLandingPromo,
    useTimedEnquirePromo,
    useTimedPromoPopup,
    showGlobalBanner,
  };
};
